import React, { useState, useEffect } from 'react'
import {
  Shield,
  ArrowLeft,
  Calendar,
  Mail,
  Building,
  Users,
  Lock,
  Globe,
  Eye
} from 'lucide-react'
import { SystemSettings, PageContent } from '../lib/storage'
import PageContentRenderer from '../components/PageContentRenderer'

const PrivacyPolicyPage = () => {
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [heroContent, setHeroContent] = useState<any>(null)
  const [pageContent, setPageContent] = useState<PageContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Load system settings
        const settingsResponse = await fetch('/api/settings-db')
        if (!settingsResponse.ok) {
          console.error('Failed to load system settings:', settingsResponse.status, await settingsResponse.text())
        } else {
          const settingsData = await settingsResponse.json()
          const settings = settingsData.success ? settingsData.result : settingsData
          if (settings && settings.id) {
            setSystemSettings(settings)
          }
        }

        // Load hero content for privacy page
        const heroResponse = await fetch('/api/hero')
        if (!heroResponse.ok) {
          console.error('Failed to load hero content:', heroResponse.status, await heroResponse.text())
        } else {
          const heroData = await heroResponse.json()
          const hero = heroData.success ? heroData.result : heroData
          setHeroContent(hero?.privacy || null)
        }

        // Load page content
        const pageResponse = await fetch('/api/admin/page-content')
        if (!pageResponse.ok) {
          console.error('Failed to load page content:', pageResponse.status, await pageResponse.text())
        } else {
          const pagesData = await pageResponse.json()
          const privacyPage = pagesData.find((p: PageContent) => p.pageId === 'privacy')
          if (privacyPage) {
            setPageContent(privacyPage)
          }
        }
      } catch (err) {
        console.error('Error loading page data:', err)
        setError('Failed to load content')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 font-semibold mb-2">Error Loading Content</p>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dynamic Theme Styles */}
      {systemSettings && (
        <style>
          {`
            :root {
              --primary-500: ${systemSettings.primaryColor || '#2E51ED'};
              --netlify-500: ${systemSettings.primaryColor || '#2E51ED'};
              --secondary-color: ${systemSettings.secondaryColor || '#05BDBA'};
            }
          `}
        </style>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                {systemSettings?.logoUrl ? (
                  <img src={systemSettings.logoUrl} alt="Logo" className="w-8 h-8 rounded" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-800 rounded flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {systemSettings?.brandName || systemSettings?.companyName || 'Netlify'}
                  </h1>
                  <p className="text-sm text-gray-600 -mt-1">Trust Center</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-primary-600 hover:text-primary-700 inline-flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Trust Center
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              {heroContent?.title || 'Privacy Statement'}
            </h1>
            {heroContent?.subtitle && (
              <h2 className="text-2xl text-primary-200 mb-4">
                {heroContent.subtitle}
              </h2>
            )}
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              {heroContent?.description ||
               'We are committed to protecting your privacy and being transparent about how we collect, use, and share your personal data.'}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-primary-300 mr-2" />
                <span>Last Updated: February 13, 2024</span>
              </div>
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-primary-300 mr-2" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Overview */}
      <section className="py-12 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Users className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Your Control</h3>
              <p className="text-sm text-gray-600">You control your personal data</p>
            </div>
            <div className="text-center">
              <Lock className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Data Security</h3>
              <p className="text-sm text-gray-600">Industry-standard protection</p>
            </div>
            <div className="text-center">
              <Eye className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Transparency</h3>
              <p className="text-sm text-gray-600">Clear data practices</p>
            </div>
            <div className="text-center">
              <Globe className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Global Compliance</h3>
              <p className="text-sm text-gray-600">GDPR and regional laws</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {pageContent ? (
            <PageContentRenderer content={pageContent} systemSettings={systemSettings} />
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading content...</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-primary-400" />
              <span className="ml-2 text-lg font-semibold">Trust Center</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your security is our priority. Access comprehensive documentation
              and evidence of our security practices.
            </p>
            <p className="text-gray-400 text-sm">
              &copy; 2024 {systemSettings?.companyName || 'Company Name'}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default PrivacyPolicyPage